import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Sparkles, Gift, Loader2, CheckCircle2, Phone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { mpSchedule } from "@/lib/metaPixel";

/**
 * /events/homecoming-2026
 * Standalone event page — Heather's Homecoming Dress Event · Aug 1-2 · at
 * the Lake Country DockHounds stadium (upper floor).
 *
 * Not a re-hosted flyer; this is Heather's own editorial page. RSVP form
 * pipes into `event_rsvps` collection and triggers a branded email + ICS.
 */
// Heather's official Facebook event page — RSVPs are auto-redirected here
// after successful signup so they can hit "Interested" on FB too.
const FB_EVENT_URL = "https://www.facebook.com/share/193dki3R5Y/";

export default function HomecomingEvent() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [guests, setGuests] = useState(1);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const rsvp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/homecoming-2026/rsvp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(), name: name.trim() || undefined,
          phone: phone.trim() || undefined,
          guest_count: Math.max(1, parseInt(guests, 10) || 1),
          source: "event-page",
        }),
      });
      const j = await r.json();
      if (r.ok && j.ok) {
        setStatus("success");
        setMessage(j.already_rsvpd ? "You're already on the list — see you Aug 1st." : "You're on the list. Watch your inbox for the event details.");
        try { window.posthog?.capture("homecoming_event_rsvp", { source: "event-page", already: !!j.already_rsvpd }); } catch { /* noop */ }
        // Meta Pixel — Schedule (event RSVP confirmed on server; skip if already RSVP'd)
        if (!j.already_rsvpd) {
          try { mpSchedule({ source: "homecoming_event_rsvp" }); } catch { /* noop */ }
        }
        // Send them to Heather's official Facebook event page after a
        // brief pause so they read the confirmation first. New tab so they
        // don't lose the boutique.
        setTimeout(() => {
          try { window.open(FB_EVENT_URL, "_blank", "noopener,noreferrer"); } catch { /* noop */ }
        }, 2400);
      } else {
        setStatus("error"); setMessage(j.detail || "Something's off. Try again in a moment?");
      }
    } catch {
      setStatus("error"); setMessage("Couldn't reach us. Try again in a minute?");
    }
  };

  return (
    <div className="bg-ink text-ivory min-h-screen" data-testid="homecoming-event-page">
      <Seo
        title="Homecoming Dress Event · Aug 1-2 · Heather's Boutique"
        description="Two days only — hundreds of homecoming dresses from $99, first 25 get a VIP bag. Lake Country DockHounds upper floor. Aug 1 & 2, 2026."
        path="/events/homecoming-2026"
        image="/heathers-h.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          "name": "Heather's Boutique Homecoming Dress Event",
          "startDate": "2026-08-01T10:00:00-05:00",
          "endDate": "2026-08-02T17:00:00-05:00",
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": "Lake Country DockHounds Stadium (Upper Floor)",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1011 Blue Ribbon Cir N",
              "addressLocality": "Oconomowoc",
              "addressRegion": "WI",
              "postalCode": "53066",
              "addressCountry": "US"
            }
          },
          "organizer": {
            "@type": "Organization",
            "name": "Heather's Boutique",
            "url": "https://wi.heathersboutique.com/",
            "telephone": "+1-888-450-6525"
          },
          "offers": {
            "@type": "Offer",
            "price": "99",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": "https://wi.heathersboutique.com/events/homecoming-2026"
          },
          "description": "Two days only. Hundreds of homecoming dresses from $99. First 25 receive a VIP bag."
        }}
      />

      {/* HERO */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#3d0f1f] via-ink to-[#5a1030]" />
        <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[#FF6B9E]/30 blur-3xl" aria-hidden />
        <div className="absolute -bottom-32 -left-40 w-[600px] h-[600px] rounded-full bg-champagne/20 blur-3xl" aria-hidden />

        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 pt-16 pb-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF6B9E]/15 border border-[#FF6B9E]/40 rounded-full mb-6">
              <Sparkles className="w-3 h-3 text-[#FF6B9E]" />
              <span className="font-label text-[10px] tracking-[0.35em] text-[#FF6B9E]">TWO DAYS ONLY · TICKETED FREE</span>
            </div>
            <p className="font-display italic text-2xl sm:text-3xl text-champagne/90">Heather's Boutique presents</p>
            <h1 className="font-display text-[64px] sm:text-[112px] leading-[0.9] tracking-tight text-[#FF6B9E] mt-3 drop-shadow-[0_2px_40px_rgba(255,107,158,0.35)]">
              Homecoming<br />Dress Event
            </h1>
            <p className="font-label text-sm sm:text-base tracking-[0.3em] text-ivory mt-6">
              AUG 1<span className="text-[#FF6B9E] mx-1">·</span>2 · 2026
            </p>
            <p className="font-body text-lg text-ivory/70 mt-6 max-w-xl mx-auto leading-relaxed">
              Two days. Hundreds of gowns. Oconomowoc's biggest homecoming preview — hosted at the Lake Country DockHounds ballpark.
            </p>
          </motion.div>

          {/* $99 marquee */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mt-14 inline-block relative"
          >
            <div className="absolute -inset-8 bg-[#FF6B9E]/20 blur-3xl rounded-full" aria-hidden />
            <div className="relative">
              <p className="font-display italic text-2xl text-champagne">Dresses starting at</p>
              <div className="font-display text-[160px] sm:text-[200px] leading-[0.85] text-[#FF6B9E] font-semibold tracking-tight">$99</div>
              <p className="font-display italic text-2xl text-champagne mt-2">Look amazing. Feel confident.</p>
              <div className="w-24 h-px bg-[#FF6B9E] mx-auto mt-6" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* DETAILS GRID */}
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-5">
          <div className="border border-ivory/15 rounded-sm p-8 bg-ivory/[0.03]">
            <Calendar className="w-6 h-6 text-[#FF6B9E] mb-4" />
            <p className="font-label text-[10px] tracking-widest text-[#FF6B9E] mb-2">WHEN</p>
            <p className="font-display text-3xl text-ivory leading-tight">Fri, Aug 1<br />+ Sat, Aug 2</p>
            <p className="font-body text-sm text-ivory/60 mt-3">10am — 5pm both days</p>
          </div>
          <div className="border border-ivory/15 rounded-sm p-8 bg-ivory/[0.03]">
            <MapPin className="w-6 h-6 text-[#FF6B9E] mb-4" />
            <p className="font-label text-[10px] tracking-widest text-[#FF6B9E] mb-2">WHERE</p>
            <p className="font-display text-3xl text-ivory leading-tight">Lake Country DockHounds</p>
            <p className="font-body text-sm text-ivory/60 mt-3">Upper Floor · 1011 Blue Ribbon Cir N,<br />Oconomowoc, WI 53066</p>
            <a href="https://www.google.com/maps/dir/?api=1&destination=Lake+Country+DockHounds+Oconomowoc+WI" target="_blank" rel="noopener noreferrer" className="mt-4 inline-block font-label text-[10px] tracking-widest text-champagne hover:text-[#FF6B9E]">GET DIRECTIONS →</a>
          </div>
          <div className="border border-[#FF6B9E]/50 rounded-sm p-8 bg-gradient-to-br from-[#FF6B9E]/20 to-champagne/10">
            <Gift className="w-6 h-6 text-[#FF6B9E] mb-4" />
            <p className="font-label text-[10px] tracking-widest text-[#FF6B9E] mb-2">FIRST 25</p>
            <p className="font-display text-3xl text-ivory leading-tight">Walk out with<br />a VIP Bag 🎁</p>
            <p className="font-body text-sm text-ivory/80 mt-3">Get in line early. First-come, first-served both days.</p>
          </div>
        </div>
      </div>

      {/* RSVP */}
      <div className="border-y border-ivory/10 bg-ivory/[0.02]">
        <div className="max-w-[720px] mx-auto px-5 sm:px-8 py-20 text-center">
          <p className="font-label text-[10px] tracking-widest text-[#FF6B9E]">◉ SAVE THE DATE</p>
          <h2 className="font-display text-5xl mt-3 leading-tight">Get on the guest list.</h2>
          <p className="font-body text-sm text-ivory/60 mt-4 max-w-md mx-auto">Drop your email and we'll send you the full details — plus a calendar invite so you don't forget. We'll text you the morning-of parking directions.</p>

          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mt-8 max-w-md mx-auto bg-champagne/10 border border-champagne/30 rounded-sm p-6 text-left"
              data-testid="event-rsvp-success"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-champagne shrink-0 mt-0.5" />
                <div>
                  <p className="font-display text-2xl text-ivory">You're on the list.</p>
                  <p className="font-body text-sm text-ivory/70 mt-1">{message}</p>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-champagne/20">
                <p className="font-label text-[9px] tracking-widest text-champagne mb-2">HIT "INTERESTED" ON HEATHER'S FACEBOOK PAGE</p>
                <p className="font-body text-xs text-ivory/60 break-all mb-3">{FB_EVENT_URL}</p>
                <a
                  href={FB_EVENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="event-rsvp-fb-link"
                  className="inline-flex items-center gap-2 bg-[#FF6B9E] hover:bg-champagne text-ink font-label text-[10px] tracking-[0.28em] px-5 py-3 rounded-sm transition-colors"
                >
                  OPEN FACEBOOK EVENT →
                </a>
                <p className="font-label text-[9px] tracking-widest text-ivory/40 mt-3">WE'LL AUTO-OPEN IT IN A SECOND</p>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={rsvp} className="mt-8 max-w-md mx-auto space-y-3 text-left" data-testid="event-rsvp-form">
              <div>
                <label className="font-label text-[9px] tracking-widest text-ivory/50">EMAIL</label>
                <input
                  type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="event-rsvp-email"
                  className="mt-1 w-full bg-ivory/[0.06] border border-ivory/20 rounded-sm px-4 py-3 font-body text-sm text-ivory placeholder:text-ivory/30 outline-none focus:border-[#FF6B9E] transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-label text-[9px] tracking-widest text-ivory/50">NAME (OPTIONAL)</label>
                  <input
                    type="text"
                    value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="event-rsvp-name"
                    className="mt-1 w-full bg-ivory/[0.06] border border-ivory/20 rounded-sm px-4 py-3 font-body text-sm text-ivory outline-none focus:border-[#FF6B9E] transition-colors"
                  />
                </div>
                <div>
                  <label className="font-label text-[9px] tracking-widest text-ivory/50">GUESTS</label>
                  <select
                    value={guests} onChange={(e) => setGuests(e.target.value)}
                    data-testid="event-rsvp-guests"
                    className="mt-1 w-full bg-ivory/[0.06] border border-ivory/20 rounded-sm px-4 py-3 font-body text-sm text-ivory outline-none focus:border-[#FF6B9E] transition-colors"
                  >
                    {[1,2,3,4,5,6].map((n) => <option key={n} value={n} className="bg-ink">{n}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="font-label text-[9px] tracking-widest text-ivory/50">PHONE (OPTIONAL · FOR DAY-OF TEXTS)</label>
                <input
                  type="tel"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="(414) 555-0000"
                  data-testid="event-rsvp-phone"
                  className="mt-1 w-full bg-ivory/[0.06] border border-ivory/20 rounded-sm px-4 py-3 font-body text-sm text-ivory placeholder:text-ivory/30 outline-none focus:border-[#FF6B9E] transition-colors"
                />
              </div>
              <button
                type="submit" disabled={status === "loading"}
                data-testid="event-rsvp-submit"
                className="w-full bg-[#FF6B9E] hover:bg-champagne text-ink font-label text-[11px] tracking-[0.3em] py-4 rounded-sm transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
              >
                {status === "loading" ? <><Loader2 className="w-4 h-4 animate-spin" /> SAVING…</> : "COUNT ME IN ✦"}
              </button>
              {status === "error" && <p className="font-label text-[10px] tracking-widest text-[#FF6B9E] text-center">{message}</p>}
              <p className="font-label text-[9px] tracking-widest text-ivory/40 text-center pt-1">FREE ENTRY · NO PURCHASE REQUIRED</p>
            </form>
          )}
        </div>
      </div>

      {/* WHY IT'S SPECIAL */}
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="font-label text-[10px] tracking-widest text-[#FF6B9E]">WHAT TO EXPECT</p>
          <h2 className="font-display text-5xl mt-3 leading-tight">The whole ballpark, dressed to the nines.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { t: "Hundreds of gowns", d: "Every color, every silhouette, every size — pulled from our Oconomowoc atelier and staged in one massive room." },
            { t: "Heather + team on-site", d: "Real stylists helping you find the one. No pressure, just eyes for what will actually look great on you." },
            { t: "Shop local. Feel amazing.", d: "Support a Wisconsin family business. Take your dress home the same day. No stress, no shipping wait." },
          ].map((c) => (
            <div key={c.t} className="border-t border-ivory/15 pt-6">
              <p className="font-display text-3xl leading-tight text-champagne italic">{c.t}</p>
              <p className="font-body text-sm text-ivory/60 mt-3 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
        <p className="text-center font-label text-[10px] tracking-[0.3em] text-ivory/50 mt-16">
          SHOP LOCAL · SUPPORT LOCAL · CELEBRATE LOCAL
        </p>
        <p className="text-center mt-8">
          <a href="tel:+18884506525" className="inline-flex items-center gap-2 font-label text-[10px] tracking-widest text-champagne hover:text-[#FF6B9E]">
            <Phone className="w-3 h-3" /> QUESTIONS? CALL (888) 450-6525
          </a>
        </p>
      </div>
    </div>
  );
}
